import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { COMPANY, DEVICE_OPTIONS } from "@/data/feniks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", device_type: "", city: "", message: "" };

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.device_type || !form.message) {
      setStatus({ state: "error", msg: "Uzupełnij wszystkie wymagane pola." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", msg: res.data.message || "Zgłoszenie wysłane." });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Wystąpił błąd. Spróbuj ponownie.";
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Wystąpił błąd." });
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="bg-white border-t border-zinc-200 py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Info left */}
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[var(--feniks-red)] mb-3">
              [05] Kontakt
            </div>
            <h2 data-testid="contact-headline" className="font-display text-3xl md:text-4xl lg:text-5xl leading-none tracking-tighter mb-5">
              Umów wizytę serwisanta.
            </h2>
            <p className="text-zinc-600 leading-relaxed mb-8 max-w-md">
              Wypełnij formularz lub zadzwoń. Odpowiadamy w godzinach pracy serwisu, najczęściej tego samego dnia.
            </p>

            <ul className="space-y-5">
              {[
                { icon: Phone, label: "Telefon", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
                { icon: Mail, label: "E-mail", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
                { icon: MapPin, label: "Adres", value: COMPANY.address },
                { icon: Clock, label: "Godziny pracy", value: COMPANY.hours },
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-zinc-300 text-zinc-700">
                    <it.icon size={16} />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{it.label}</div>
                    {it.href ? (
                      <a href={it.href} className="font-display text-base md:text-lg tracking-tight hover:text-[var(--feniks-red)] transition-colors break-all">
                        {it.value}
                      </a>
                    ) : (
                      <div className="font-display text-base md:text-lg tracking-tight">{it.value}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Form right */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-zinc-200 p-6 md:p-10 bg-zinc-50"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-700 mb-2">Imię i nazwisko *</label>
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    className="w-full bg-white border border-zinc-300 px-4 py-3 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none transition"
                    placeholder="Jan Kowalski"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-700 mb-2">Telefon *</label>
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full bg-white border border-zinc-300 px-4 py-3 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none transition"
                    placeholder="+48 ..."
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-700 mb-2">E-mail *</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="w-full bg-white border border-zinc-300 px-4 py-3 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none transition"
                    placeholder="email@domena.pl"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-700 mb-2">Miasto</label>
                  <input
                    data-testid="contact-input-city"
                    value={form.city}
                    onChange={update("city")}
                    className="w-full bg-white border border-zinc-300 px-4 py-3 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none transition"
                    placeholder="Katowice"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-700 mb-2">Typ urządzenia *</label>
                  <select
                    data-testid="contact-select-device"
                    value={form.device_type}
                    onChange={update("device_type")}
                    className="w-full bg-white border border-zinc-300 px-4 py-3 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none transition"
                  >
                    <option value="">— wybierz —</option>
                    {DEVICE_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-700 mb-2">Opis usterki *</label>
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    className="w-full bg-white border border-zinc-300 px-4 py-3 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 outline-none transition resize-y"
                    placeholder="Np. Pralka Bosch, model WAB28..., wyświetla kod E18, nie odpompowuje wody."
                  />
                </div>
              </div>

              {status.state === "success" && (
                <div data-testid="contact-success" className="mt-5 flex items-start gap-3 border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  <CheckCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}
              {status.state === "error" && (
                <div data-testid="contact-error" className="mt-5 flex items-start gap-3 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}

              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={status.state === "loading"}
                className="mt-6 inline-flex items-center justify-center gap-3 bg-[var(--feniks-red)] text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-[var(--feniks-red-hover)] disabled:opacity-60 transition-colors w-full sm:w-auto"
              >
                {status.state === "loading" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {status.state === "loading" ? "Wysyłanie..." : "Wyślij zgłoszenie"}
              </button>

              <p className="text-xs text-zinc-500 mt-4">
                Wysyłając formularz wyrażasz zgodę na kontakt w sprawie zgłoszenia serwisowego.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
